import { Stack, Typography, Link, useMediaQuery, Divider } from '@mui/material';

import AppStore from '../../../../../assets/icons/AppStore';
import GooglePlay from '../../../../../assets/icons/GooglePlay';
import PhoneIcon from '../../../../../assets/icons/Phone';
import PhoneNumberLink from '../../../../../components/styledComponents/PhoneNumberLink';
import colors from '../../../../../constants/colors';
import useAnalytics from '../../../../../hooks/useAnalytics';
import theme from '../../../../../styles/theme';

const MySafeDriveCoveragesCard = () => {
  const { track } = useAnalytics();
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('tablet'));
  return (
    <Stack
      sx={{
        backgroundColor: 'white',
        p: { mobile: 2, tablet: 3 },
        boxShadow: `inset 0px 0px 0px 1px ${colors.csaaNeutral}`,
        borderRadius: 2,
      }}
    >
      <Stack sx={{ mb: { mobile: 3, tablet: 2 } }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { mobile: 16, tablet: 18 },
            lineHeight: '24px',
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            color: colors.csaaBlack,
            letterSpacing: '0.4px',
          }}
        >
          MySafeDrive™
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: { mobile: 14, tablet: 16 },
            lineHeight: '24px',
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            color: colors.csaaDark,
          }}
        >
          All participating drivers:
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: { mobile: 14, tablet: 16 },
            lineHeight: '24px',
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            color: colors.csaaDark,
          }}
        >
          View driving results and trip history in the MySafeDrive™ app.
        </Typography>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: { mobile: 14, tablet: 16 },
            lineHeight: '24px',
            fontFamily: 'Cabin',
            fontKerning: 'normal',
            color: colors.csaaDark,
            mb: 2,
          }}
        >
          Don’t have it yet? Download the app and register.
        </Typography>
        <Stack spacing={2} direction="row">
          <AppStore
            onClick={() => {
              track('Link Accessed', {
                link: 'Apple Store',
                location: 'MySafeDrive Card',
                event_type: 'Link Accessed',
              });
            }}
          />
          <GooglePlay
            onClick={() => {
              track('Link Accessed', {
                link: 'Google Play Store',
                location: 'MySafeDrive Card',
                event_type: 'Link Accessed',
              });
            }}
          />
        </Stack>
      </Stack>
      <Stack sx={{ mb: { mobile: 3, tablet: 2 } }}>
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 700,
            color: colors.csaaMid,
          }}
        >
          Questions?
        </Typography>
        <Link
          sx={{
            fontFamily: 'Cabin',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 400,
          }}
          data-testid="mySafeDriveLearnMoreLink"
          href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/mysafedrive/main.html"
          target="_blank"
          underline="none"
          onClick={() => {
            track('Link Accessed', {
              link: 'Learn more',
              location: 'MySafeDrive Card',
              event_type: 'Link Accessed',
            });
          }}
        >
          Learn more
        </Link>
      </Stack>
      <Stack
        sx={{
          mb: 2,
          fontWeight: 700,
          fontSize: 16,
          lineHeight: '21px',
          fontFamily: 'RT Raleway',
          fontKerning: 'normal',
          color: colors.csaaDark,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontSize: { mobile: 14, tablet: 16 },
            fontWeight: 700,
            color: colors.csaaMid,
          }}
        >
          Contact
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <PhoneIcon />
          <PhoneNumberLink
            data-testid="mySafeDrivePhoneNumber"
            phoneNumber="18008144684"
            formattedPhoneNumber="800.814.4684"
            sx={{
              fontWeight: 700,
              fontSize: { mobile: 14, tablet: 16 },
              lineHeight: '21px',
              fontFamily: 'RT Raleway',
              fontKerning: 'normal',
              color: colors.csaaSpectrumBlue50,
            }}
            onClick={() =>
              track('Contact Accessed', {
                dialed_number: '800.814.4684',
                event_type: 'Link Accessed',
                link: '800.814.4684',
                location: 'MySafeDrive Card',
                method: 'Call',
              })
            }
          />
          , Monday – Friday 5 am to 9 pm PT
        </Stack>
      </Stack>
      <Stack direction={{ mobile: 'column', tablet: 'row' }}>
        <Link
          sx={{
            color: 'primary',
            fontWeight: 400,
            fontSize: { mobile: 14, tablet: 18 },
            fontFamily: 'Cabin',
            mr: { mobile: 0, tablet: 2 },
            whiteSpace: 'nowrap',
          }}
          underline="none"
          href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/mysafedrive/mobile/terms-and-conditions.html"
          target="_blank"
          onClick={() =>
            track('Terms and Conditions Accessed', {
              event_type: 'Link Accessed',
              link: 'MySafeDrive™ Terms of Use',
              location: 'Footer',
            })
          }
        >
          MySafeDrive™ Terms of Use
        </Link>
        {!mobileBreakpoint && (
          <Divider
            orientation="vertical"
            sx={{
              height: '1rem',
              ml: { mobile: 0, tablet: '1px', desktop: '1px' },
              mr: { mobile: 0, tablet: '1px', desktop: '1px' },
              m: 'auto 0',
            }}
          />
        )}
        <Link
          sx={{
            color: 'primary',
            fontWeight: 400,
            fontSize: { mobile: 14, tablet: 18 },
            fontFamily: 'Cabin',
            ml: { mobile: 0, tablet: 2 },
            whiteSpace: 'nowrap',
          }}
          underline="none"
          href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/mysafedrive/mobile/privacy-notice.html"
          target="_blank"
          onClick={() =>
            track('Terms and Conditions Accessed', {
              event_type: 'Link Accessed',
              link: 'MySafeDrive™ Privacy Policy',
              location: 'Footer',
            })
          }
        >
          MySafeDrive™ Privacy Policy
        </Link>
      </Stack>
    </Stack>
  );
};

export default MySafeDriveCoveragesCard;
